/* eslint-disable camelcase */
import { useCallback, useRef, useState } from "react";
import { Snackbar } from "@root/shared/components/Snackbar";
import { axios, type AxiosError, type AxiosResponse } from "@root/libs/axios";
import { sendErrorLog } from "@root/shared/utils/sendErrorLog";
import { type BusinessTypeOptions } from "./uiParts/BusinessTypeSelector";
import type { BusinessTypeFilterOption, FormValues, OptionProps } from "./types";
import { useWorksCount } from "./useWorksCount";
import { useAreaData } from "./useAreaData";
import { useSalaryData } from "./useSalaryData";
import { Presentation } from "./Presentation";
import { HiddenForm } from "./HiddenForm";
import { defaultFormValues } from "./constants";
import { type SearchConditionsConfig } from "./uiParts/SearchConditionsModal";

export type SearchFormProps = {
  registrationStep1Url: string;
  loginUrl: string;
  isLoggedIn: boolean;
  isSp: boolean;
  isBiyo: boolean;
  action: string;
  countUrl: string;
  citiesUrl: string;
  linesUrl: string;
  popularAreasUrl: string;
  prefectureOptions: OptionProps[];
  businessTypeOptions: BusinessTypeOptions[];
  employmentTypeOptions: BusinessTypeFilterOption[];
  salaryTypeOptions: OptionProps[];
  salaryMoneyOptionsUrl: string;
  workingDayOptions: OptionProps[];
  holidayOptions: OptionProps[];
  workstyleOptions: OptionProps[];
  preferentialConditionsOptions: BusinessTypeFilterOption[];
  occupationOptions: BusinessTypeFilterOption[];
  facilityTypeOptions: BusinessTypeFilterOption[];
  customerTypeOptions: BusinessTypeFilterOption[];
  featureOptions: BusinessTypeFilterOption[];
  experienceOptions: BusinessTypeFilterOption[];
  welfareOptions: OptionProps[];
  workDescriptionOptions: BusinessTypeFilterOption[];
  userCredentialOptions: BusinessTypeFilterOption[];
  distanceMeter?: string;
  saveUrl: string;
  savedConditions: SearchConditionsConfig;
  searchedConditions: SearchConditionsConfig;
  prevSearchFormValues?: FormValues;
  appDlUrl: string;
  appImgSrc: string;
  appGaLabel: string;
};

export const SearchForm = (props: SearchFormProps) => {
  const {
    registrationStep1Url,
    loginUrl,
    isLoggedIn,
    isSp,
    isBiyo,
    action,
    countUrl,
    citiesUrl,
    linesUrl,
    popularAreasUrl,
    prefectureOptions,
    businessTypeOptions,
    employmentTypeOptions,
    salaryTypeOptions,
    salaryMoneyOptionsUrl,
    workingDayOptions,
    holidayOptions,
    workstyleOptions,
    preferentialConditionsOptions,
    occupationOptions,
    facilityTypeOptions,
    customerTypeOptions,
    featureOptions,
    experienceOptions,
    welfareOptions,
    workDescriptionOptions,
    userCredentialOptions,
    distanceMeter,
    saveUrl,
    savedConditions: defaultSavedConditions,
    searchedConditions,
    prevSearchFormValues,
    appDlUrl,
    appImgSrc,
    appGaLabel,
  } = props;

  const [formValues, setFormValues] = useState<FormValues>(
    prevSearchFormValues ?? defaultFormValues,
  );
  const [savedConditions, setSavedConditions] =
    useState<SearchConditionsConfig>(defaultSavedConditions);

  const handleFormValuesChange = (updatedValues: Partial<FormValues>) =>
    setFormValues((prev) => ({ ...prev, ...updatedValues }));

  const handleClear = () => {
    setFormValues(defaultFormValues);
  };

  const formRef = useRef<HTMLFormElement>(null);
  const handleSearch = () => formRef.current?.submit();

  const { currentWorksCount, isAnimating } = useWorksCount({
    countUrl,
    formValues,
    distanceMeter,
  });

  const [messages, setMessages] = useState<string[]>([]);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const handleError = useCallback((message: string) => {
    setErrorMessages((prev) => [...prev, message]);
    sendErrorLog(new Error(message));
  }, []);

  const handleSave = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries({ ...formValues, "q[distance_meter]": distanceMeter ?? "" }).forEach(
        ([name, value]) => {
          if (Array.isArray(value)) {
            value.forEach((v) => params.append(name, String(v)));
          } else if (value !== undefined && value !== null && value !== "") {
            // NOTE: 既存APIを整備済みで、valueがあるkeyのみをparamsに含めれば問題ないことを確認済み。 https://rejob.slack.com/archives/C08SQQBN4S2/p1761299878447189?thread_ts=1761187837.375139&cid=C08SQQBN4S2
            params.append(name, String(value));
          }
        },
      );

      const res = await axios.post<
        { query: string },
        AxiosResponse<{
          status: number;
          searchedAt: string;
          location?: string;
          work?: string;
          otherInfo?: string;
          searchUrl: string;
          keywords?: string;
          message?: string;
        }>
      >(saveUrl, {
        query: params.toString(),
      });

      setSavedConditions((prev) => ({
        ...prev,
        conditions: [{ ...res.data }, ...prev.conditions],
      }));
      setMessages(["保存に成功しました。お気に入り検索条件の編集はマイページで行ってください。"]);
    } catch (e) {
      const error = e as AxiosError<{ message?: string }>;
      const message =
        error?.response?.data.message ??
        (error instanceof Error ? error.message : "保存に失敗しました");

      handleError(message);
    }
  };

  const {
    popularAreaChoices,
    cityChoices,
    lineChoices,
    isLoading: isAreaDataLoading,
    isError: isAreaDataError,
  } = useAreaData({
    prefectureValue: formValues["q[pref]"],
    citiesUrl,
    linesUrl,
    popularAreasUrl,
    isBiyo,
    onError: handleError,
  });

  const {
    salaryMinOptions,
    salaryMaxOptions,
    isLoading: isSalaryDataLoading,
  } = useSalaryData({
    selectedSalaryType: formValues["q[salary_type]"],
    salaryMoneyOptionsUrl,
    onError: handleError,
  });

  return (
    <>
      <HiddenForm
        formRef={formRef}
        action={action}
        values={formValues}
        distanceMeter={distanceMeter ?? ""}
      />
      <Presentation
        registrationStep1Url={registrationStep1Url}
        loginUrl={loginUrl}
        isLoggedIn={isLoggedIn}
        isSp={isSp}
        isBiyo={isBiyo}
        currentWorksCount={currentWorksCount}
        isAnimating={isAnimating}
        formValues={formValues}
        onFormValuesChange={handleFormValuesChange}
        onClear={handleClear}
        cityChoices={cityChoices}
        lineChoices={lineChoices}
        popularAreaChoices={popularAreaChoices}
        prefectureOptions={prefectureOptions}
        businessTypeOptions={businessTypeOptions}
        employmentTypeOptions={employmentTypeOptions}
        salaryTypeOptions={salaryTypeOptions}
        salaryMinOptions={salaryMinOptions}
        salaryMaxOptions={salaryMaxOptions}
        isSalaryDataLoading={isSalaryDataLoading}
        workingDayOptions={workingDayOptions}
        holidayOptions={holidayOptions}
        workstyleOptions={workstyleOptions}
        preferentialConditionsOptions={preferentialConditionsOptions}
        occupationOptions={occupationOptions}
        facilityTypeOptions={facilityTypeOptions}
        customerTypeOptions={customerTypeOptions}
        featureOptions={featureOptions}
        experienceOptions={experienceOptions}
        welfareOptions={welfareOptions}
        workDescriptionOptions={workDescriptionOptions}
        userCredentialOptions={userCredentialOptions}
        isActionButtonDisabled={!formValues["q[pref]"] || isAreaDataError || isAreaDataLoading}
        onSearch={handleSearch}
        onSave={handleSave}
        savedConditions={savedConditions}
        searchedConditions={searchedConditions}
        appDlUrl={appDlUrl}
        appImgSrc={appImgSrc}
        appGaLabel={appGaLabel}
      />
      {messages[0] && (
        <Snackbar
          key={messages[0]}
          isOpened
          message={messages[0]}
          onClose={() => setMessages((prev) => prev.slice(1))}
        />
      )}
      {errorMessages[0] && (
        <Snackbar
          key={errorMessages[0]}
          isOpened
          message={errorMessages[0]}
          onClose={() => setErrorMessages((prev) => prev.slice(1))}
        />
      )}
    </>
  );
};
